// client/src/contexts/AuthContext.js
"use client";

import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
// Lazy-resolved so the @supabase/ssr SDK chunk (~970 KB uncompressed) only
// loads when one of the auth call sites below actually needs the client -
// never during the first paint of an authenticated route. DEL-771.
import { getSupabase } from '../supabaseClient';
import { setCustomToken, clearCachedAccessToken } from '@/utils/accessToken';
import { apiRequest } from '@/utils/api';
import { storeUTMParameters, trackEvent } from '@/utils/analytics';
import { applyStoredReferralCode } from '@/utils/referralLinks';
import { trackExperimentConversions } from '@/utils/experimentTracking';
import { setPostHogUserId, resetPostHogUser, getPostHogSessionId, trackSessionEnded, markSessionStarted, syncPostHogConsent } from '@/analytics/posthog';
import { grantAnalyticsConsentForUser } from '@/contexts/CookieConsentContext';
import { getUTMForAnalytics } from '@/utils/utmTracking';
import axios from 'axios';
import Cookies from 'js-cookie';
import { installCsrfInterceptors } from '@/utils/csrfToken';

// Plain `axios` mutations (the onboarding steps among them) are cookie-
// authenticated after any hard navigation, so they need the CSRF token that
// monitoredAxios already sends. Installed here because this module loads on
// every page.
installCsrfInterceptors(axios);

// DEL-1019: skip the /api/auth/me check for anonymous visitors on public
// marketing/blog routes. The endpoint 401s for non-authenticated users, which
// (a) emits a browser-native "Failed to load resource: ... 401" console
// message and (b) trips `[RequestMonitor] ... 401` from monitoredAxios.ts -
// the same $exception pair DEL-994 already filters out of PostHog's event
// stream but NOT out of PostHog session recordings (rrweb records DOM, not
// events). Public pages render identically for signed-in and signed-out
// visitors (PublicNavbar vs navbar aside), so we don't need to ask the server
// "are you signed in?" when we already know the answer. This list MUST stay
// in sync with LayoutClient.tsx PUBLIC_PREFIXES (see DEL-1019 plan).
const AUTH_ME_SKIPPABLE_PUBLIC_PREFIXES = [
  '/features', '/guides', '/help', '/comparison',
  '/api', '/blog', '/reassurance', '/stats',
  // DEL-1012: /pricing fires /api/stripe/prices + /api/public-stats/stats
  // (both already public), but anonymous visitors also trigger
  // /api/auth/me -> 401, which leaks into PostHog session recordings as
  // AxiosError / AuthSessionMissingError / Failed-to-load-resource. LayoutClient
  // already treats /pricing as public (PUBLIC_ROUTES), so skipping /me here
  // keeps the navbar-vs-PublicNavbar decision the same (user stays null on
  // anonymous visits regardless; SSR initialUser carries signed-in users
  // through x-user-token).
  '/pricing',
  // DEL-995: the homepage root '/' is the only public route still missing.
  // Anonymous visitors to '/' fired GET /api/auth/me -> 401, which surfaced as
  // a native "Failed to load resource: 401" console error and an AxiosError in
  // PostHog session recordings.
  //
  // Use '/', never ''. The matcher below is
  // `pathname === prefix || pathname.startsWith(prefix + '/')`, so:
  //   '/'  -> matches the homepage root ONLY; every other route still calls.
  //   ''  -> `pathname.startsWith('/')` is true for EVERY path, which would
  //          skip the auth check on /dashboard, /simulations, /income and
  //          break every signed-in user on a protected route.
  //
  // Safe for signed-in visitors: SSRLayout is mounted in the root layout and
  // passes initialUser from the x-user-token header, and AuthProvider's first
  // useEffect early-returns on initialUser — so a signed-in user on '/' already
  // has their state and never needed this round-trip. Only the anonymous
  // visitor is affected, which is the case we are fixing.
  '/',
  // DEL-1812: /login is the route where email sign-in / sign-up happens. The
  // AuthProvider used to fire /api/auth/me on the /login mount, which 401'd
  // for the anonymous visitor that the page is actually built for. That 401
  // was harmless on its own, but after a successful sign-in the soft-
  // navigated /dashboard mount reuses the same AuthProvider instance and /me
  // is never re-fired with the new auth_session cookie — so the post-signin
  // dashboard never received emailVerified and the new sign-up was rendered
  // without a "verify your email" prompt or the trial/onboarding surface. By
  // skipping the pre-signin /me round-trip entirely, the only /api/auth/me
  // call on the signup flow is the post-signin refetch in signInWithEmail,
  // which returns 200 (the cookie is now committed) and brings emailVerified
  // into AuthContext.user. Safe for already-signed-in visitors too: SSRLayout
  // restores them via initialUser before the client mount.
  '/login',
  // 2026-10-18: public calculators, tools and legal/content pages. Anonymous
  // visitors to /uk-fire-calculator (and every page below) fired
  // /api/auth/me -> 401, caught by uk-fire-calculator.spec.ts "should load
  // without errors". These are the routes LayoutClient already treats as public
  // (PUBLIC_ROUTES), minus /verify-email, which is part of the sign-in flow.
  // Signed-in visitors are unaffected: SSRLayout restores them via initialUser.
  '/uk-fire-calculator', '/tools', '/calculators', '/security', '/terms', '/privacy', '/cookies', '/why-delphina-exists', '/glossary', '/income-tax-calculator', '/compound-interest-calculator', '/compound-interest', '/fire-calculator', '/isa-calculator', '/mortgage-calculator', '/pension-calculator', '/pension-contribution-calculator', '/savings-calculator', '/savings-goal', '/retirement-planner', '/stamp-duty', '/student-loan-repayment', '/cost-of-living', '/safe-withdrawal-rate', '/savings-rates', '/will-i-run-out-of-money', '/take-home-pay', '/mortgage-payoff', '/monte-carlo-retirement-calculator',
  // 2026-10-18: every other public marketing/tool route that still fired an
  // anonymous /api/auth/me -> 401, found by visiting all 74 public top-level
  // routes signed out (where-can-i-retire-map.spec.ts caught the first).
  '/affiliates', '/ai-information', '/am-i-on-track', '/are-you-on-track', '/book', '/brokers', '/can-i-retire-at', '/coaching', '/compound-growth', '/employee-benefits', '/financial-clarity-check', '/financial-coaching', '/financial-therapy', '/fire', '/how-much-pension-do-i-need', '/inheritance-planning-check', '/is-delphina-legit', '/isa-health-check', '/isa-vs-sipp', '/landlords', '/loans', '/locations', '/money-management', '/mortgage-or-invest', '/mortgage-vs-savings', '/pension-pot-by-age', '/pension-providers', '/pension-shortfall-calculator', '/personal-finance', '/press', '/pricing-comparison', '/research', '/retirement-readiness-check', '/reviews', '/state-pension-estimate', '/videos', '/where-can-i-retire-today', '/whitepaper',
];

function isAuthMeSkippablePublicPath(pathname: string | null): boolean {
  return !!pathname && AUTH_ME_SKIPPABLE_PUBLIC_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix + '/')
  );
}
// @simplewebauthn/browser is only used inside signInWithPasskey (a button
// click handler). Importing it statically pulled its WebAuthn client into
// every route that mounts AuthProvider, including /dashboard. Loading it
// lazily via require() inside the function body keeps it out of the entry
// bundle. DEL-892.
import { User } from '@/types/User';

interface ImpersonationSession {
  id: string;
  targetUserId: string;
  targetEmail: string;
  startedAt: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  // True once the initial session check has actually answered. `loading` also
  // goes false when the 3s safety timer fires, so `!user && !loading` can mean
  // "still waiting on a slow /me" - redirect on this instead.
  authChecked: boolean;
  impersonationSession: ImpersonationSession | null;
  startImpersonation: (targetUserId: string, targetEmail: string) => Promise<void>;
  endImpersonation: () => Promise<void>;
  checkImpersonationStatus: () => Promise<void>;
  signInWithGoogle: () => Promise<{ data: unknown; error: unknown } | null>;
  signInWithApple: () => Promise<{ data: unknown; error: unknown } | null>;
  signInWithEmail: (email: string, password: string) => Promise<unknown>;
  signInWithPasskey: (email: string) => Promise<unknown>;
  checkPasskeyAvailability: (email: string) => Promise<boolean>;
  signUpWithEmail: (email: string, password: string) => Promise<unknown>;
  // reason: why the session ended (see trackSessionEnded). Defaults to
  // 'unspecified' so an untagged caller still shows up in PostHog.
  signOut: (reason?: string, detail?: Record<string, unknown>) => Promise<void>;
  logout: () => Promise<void>;
  // Lazy getter for the Supabase client. Components that need the SDK
  // (e.g., password reset, sign-out error recovery) should call this instead
  // of importing `@/supabaseClient` directly. The SDK chunk loads on first
  // call, not on first render. DEL-771.
  getSupabase: () => Promise<ReturnType<typeof getSupabase> extends Promise<infer C> ? C : never>;
}

const AuthContext = createContext<AuthContextType | null>(null);

/**
 * Read the identity out of the `token` cookie's JWT. Only the server's
 * `authenticateToken` decides whether the token is actually honoured - these
 * claims just tell the UI who it is rendering for, so a signed-in user survives
 * a page reload without waiting on a network round-trip.
 */

/**
 * DEL-892: returns true if the browser has a Supabase session token in
 * localStorage. The key is `sb-${projectHost}-auth-token` (see
 * @supabase/supabase-js `defaultStorageKey`), so we scan for the well-known
 * `sb-` prefix + `-auth-token` suffix rather than hard-coding the project
 * hostname. Used by AuthContext to defer the @supabase/ssr SDK load until a
 * sign-in has actually stored a token - keeps the SDK chunk out of the
 * /dashboard initial bundle for unauthenticated visitors.
 */
function hasSupabaseBrowserSession(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith('sb-') && key.endsWith('-auth-token')) {
        return true;
      }
    }
  } catch {
    // localStorage can throw in private modes or with strict cookie/storage
    // policies. Treat that as "no browser session" and fall back to the
    // /api/auth/me path.
  }
  return false;
}

function decodeTokenClaims(token: string): User | null {
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;

    const claims = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    if (!claims.sub) return null;
    if (typeof claims.exp === 'number' && claims.exp * 1000 <= Date.now()) return null;

    return { id: claims.sub, email: claims.email, created_at: claims.created_at };
  } catch {
    return null;
  }
}

// GET /api/auth/me, retrying anything that isn't a definitive answer. Only a
// 401/403/404 means "not signed in"; a 5xx or a dropped connection says nothing
// about the session. Treating one as signed-out settled authChecked with no
// user, and PrivateRoute sent a signed-in user to the home page before a
// parallel /me call came back 200 (seen in e2e: a dev-proxy 500 in 73ms beat
// the good response by 170ms).
//
// The caller passes an AbortSignal so we can short-circuit on page teardown.
// DEL-813: on Mobile Safari an aborted XHR fires `request.onerror` (with an
// empty message) instead of `request.onabort`, so axios rejects with
// `AxiosError: Network Error` and PostHog's XHR-patched exception autocapture
// records it as `$exception` even though our useEffect swallows the rejection.
// Treating the cancellation as terminal here means a teardown during a fast
// /login bounce never reaches PostHog. The `__CANCEL__` check mirrors
// `axios.isCancel()` (axios 1.x sets both on AbortController-driven aborts) so
// we don't retry a request the browser has already killed.
//
// DEL-868: do NOT pass `timeout: 5000` here. The 5 s ceiling was added in
// DEL-813 as "defense in depth" against a hanging /api/auth/me, but the
// fresh prod build (next start on port 3001) reliably takes longer than
// 5 s for /api/auth/me on a cold browser context immediately after sign-in,
// because (a) the post-signIn SSR/hydrate path hasn't warmed the
// auth-session route yet, and (b) the user-state restoration on /dashboard
// is the only way PrivateRoute knows the user is signed in. A 5 s ceiling
// turns a slow first request into "definitively signed out" + a redirect
// to /, which `clickUntilDetachedLands` then escalates to a 30 s
// `attemptLogin` deadline exhaustion (the failure mode DEL-868 documents).
// The AbortController above is the cancellation primitive we need; a
// request that genuinely hangs will be aborted on the next AuthProvider
// unmount or via the existing dev-proxy warmup retry path. Keep the
// timeout off unless we add monitoring that proves prod /me latency
// actually exceeds it.
async function fetchCurrentUser(signal?: AbortSignal) {
  for (let attempt = 0; ; attempt++) {
    try {
      return await axios.get('/api/auth/me', { withCredentials: true, signal });
    } catch (error) {
      if (axios.isCancel(error) || (error as { __CANCEL__?: boolean })?.__CANCEL__) {
        throw error;
      }
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;
      const definitive = status === 401 || status === 403 || status === 404;
      if (definitive || attempt >= 2) throw error;
      await new Promise((resolve) => setTimeout(resolve, 300 * (attempt + 1)));
    }
  }
}

// The HttpOnly `auth_session` cookie holds the Supabase access token (~1h)
// but lives for 7 days. Axios calls carry the fresh in-memory bearer, but
// cookie-only requests (Next route handlers such as /api/stripe-checkout,
// server-rendered pages) keep sending the expired token and get 401
// "Invalid token". Re-mint the cookie whenever Supabase refreshes the token.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function syncSessionCookie(session: any) {
  fetch('/api/auth/set-session', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({
      access_token: session.access_token,
      refresh_token: session.refresh_token,
    }),
  }).catch((err) => console.warn('[Auth] Failed to sync session cookie:', err));
}

// supabase-js refreshes an expired session during client init, before our
// listener is attached, so that refresh arrives as INITIAL_SESSION rather
// than TOKEN_REFRESHED. A token minted in the last few minutes means it did.
function wasJustIssued(accessToken: string): boolean {
  try {
    const payload = JSON.parse(atob(accessToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.iat === 'number' && Date.now() / 1000 - payload.iat < 5 * 60;
  } catch {
    return false;
  }
}

export function AuthProvider({ children, initialUser = null }: {
  children: React.ReactNode;
  initialUser?: User | null;
}) {
  const [user, setUser] = useState<User | null>(initialUser);
  const [loading, setLoading] = useState(initialUser ? false : true);
  const [authChecked, setAuthChecked] = useState(!!initialUser);
  const [impersonationSession, setImpersonationSession] = useState<ImpersonationSession | null>(null);
  const signOutRef = useRef<() => Promise<void>>(undefined);
  // onAuthChange is registered once with empty deps, so its closure would
  // capture the initial `user` forever. This ref gives it the current value.
  const userRef = useRef<User | null>(initialUser);
  const pathname = usePathname();

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // DEL-1829: bind the PostHog person to the auth state.
  //
  // `initPostHog` sets `person_profiles: 'identified_only'`, so PostHog only
  // creates a person record after identify(). The whole auth funnel
  // (login_viewed / auth_attempted / auth_succeeded / sign_up) is captured
  // around this transition, and without an identify() call every one of those
  // events stayed attached to the anonymous distinct_id with no person to
  // attribute them to. The two exported helpers existed but had no caller.
  //
  // Driven off `user` rather than the individual sign-in paths so it also
  // covers sign-in by any other route (OAuth callback, email verification,
  // passkey) and the already-signed-in-on-load case, which is where the
  // identity is set on a returning visit.
  //
  // The anonymous branch is deliberately NOT an unconditional reset. `posthog.
  // reset()` does not just drop an identified person - it mints a NEW anonymous
  // distinct_id. Resetting on the first mount of an anonymous visitor therefore
  // rotated the id *after* the page's own effects had captured against the
  // original one: React commits effects children-first, so LoginClient's
  // `login_viewed` landed on the discarded id while `auth_attempted` /
  // `auth_succeeded` landed on the replacement. identify() merges only the
  // current id's history into the person, so the pre-sign-in half of the
  // funnel was orphaned - the exact breakage this effect exists to repair.
  //
  // So reset only on a genuine sign-out (someone identified -> anonymous), which
  // is the case that needs it: a shared device must not attribute the next
  // person's events to whoever signed in before them.
  //
  // Known edge, accepted: if a user signs out and closes the tab, the next
  // anonymous mount skips the reset and PostHog's persisted distinct_id is
  // still the previous person's. Closing that would need a persisted marker
  // rather than in-memory state; it trades a real funnel break for a narrow
  // stale-id window, which is the right way round for an analytics fix.
  const posthogIdentifiedRef = useRef(false);

  useEffect(() => {
    if (user?.id) {
      posthogIdentifiedRef.current = true;
      markSessionStarted();
      // Signing in accepts analytics cookies, once per user per browser (see
      // grantAnalyticsConsentForUser). Switch PostHog over before identify().
      const userId = user.id;
      const email = user.email;
      const granted = grantAnalyticsConsentForUser(userId);
      Promise.resolve(granted ? syncPostHogConsent() : undefined).then(() => setPostHogUserId(userId, email));
    } else if (posthogIdentifiedRef.current) {
      posthogIdentifiedRef.current = false;
      resetPostHogUser();
    }
  }, [user?.id]);

  // One-time cleanup of the retired `token` cookie. Nothing writes it any more
  // and nothing reads it, but a browser that signed in before this shipped is
  // still holding a JWT in JS-readable storage for up to seven days - which is
  // precisely the exposure the HttpOnly migration set out to remove. Clear it
  // on mount rather than waiting for it to expire.
  useEffect(() => {
    if (Cookies.get('token')) {
      Cookies.remove('token');
      Cookies.remove('token', { path: '/' });
    }
  }, []);

  useEffect(() => {
    // Skip initial auth check if we have SSR user data
    if (initialUser) {
      console.log('[Auth] Skipping initial auth check - using SSR user data');
      setLoading(false);
      return;
    }

    // DEL-892: gate the Supabase SDK load behind a localStorage probe so
    // unauthenticated visitors never download the ~190 KB gzipped
    // @supabase/ssr chunk on first paint of /dashboard. DEL-771 made
    // `getSupabase()` lazy at the module level, but the mount-time session
    // check + onAuthStateChange listener IIFE in this useEffect called
    // getSupabase() unconditionally, which materialised the SDK chunk
    // anyway. Short-circuit both when there is no browser-side supabase
    // session token. The HttpOnly-cookie email flow still restores auth
    // state via /api/auth/me (no SDK required).
    //
    // @supabase/supabase-js builds the storage key as
    // `sb-${hostname.split('.')[0]}-auth-token` (e.g.
    // `sb-pskbluxjgymiggovljjd-auth-token`). We don't know the project
    // hostname at compile time without instantiating the client, so scan
    // localStorage for any matching `sb-*-auth-token` key instead of
    // hard-coding one. The key always ends in `-auth-token` and starts
    // with `sb-`, so a 5-char prefix + suffix check is sufficient.
    if (typeof window === 'undefined' || !hasSupabaseBrowserSession()) {
      // No browser-side supabase session. Defer everything to the post-paint
      // /api/auth/me fallback path so the SDK chunk is never fetched by
      // unauthenticated visitors. The 3-second safety timer still runs to
      // unblock spinners.
      //
      // DEL-1019: skip /api/auth/me entirely when we're on a public marketing
      // / pricing / guides / blog route. The endpoint 401s for non-authenticated
      // users, and the browser-native + [RequestMonitor] + AxiosError trio of
      // console messages that fires from a 401 lands in PostHog session
      // recordings even though DEL-994 filters it out of the event stream.
      // Public pages render identically for signed-in and signed-out visitors
      // (PublicNavbar vs navbar aside), so the round-trip adds noise without
      // changing behaviour. If the user later signs in via the Supabase
      // listener path (which is only registered when there IS a browser
      // Supabase session), it picks up the new state.
      if (isAuthMeSkippablePublicPath(pathname)) {
        const timeoutId = setTimeout(() => {
          setLoading(false);
        }, 3000);
        setAuthChecked(true);
        setLoading(false);
        return () => clearTimeout(timeoutId);
      }
      const noSupabaseController = new AbortController();
      const timeoutId = setTimeout(() => {
        setLoading(false);
      }, 3000);
      fetchCurrentUser(noSupabaseController.signal)
        .then((meResponse) => {
          if (meResponse.data && meResponse.data.id) {
            setUser({
              id: meResponse.data.id,
              email: meResponse.data.email,
              created_at: meResponse.data.createdAt ?? meResponse.data.created_at,
              emailVerified: meResponse.data.emailVerified === true,
            });
          }
        })
        .catch(() => {
          // /me said the user isn't signed in (401/403/404), or kept failing
          // after retries - either way, no session.
        })
        .finally(() => {
          clearTimeout(timeoutId);
          setLoading(false);
          setAuthChecked(true);
        });
      // Return a cleanup that aborts /api/auth/me on unmount so a fast
      // /login bounce doesn't reach PostHog as a Network Error (DEL-813).
      return () => {
        noSupabaseController.abort();
      };
    }

    // DEL-813: scope the /api/auth/me request to this AuthProvider instance so
    // unmount aborts it cleanly. Without an AbortSignal the XHR is killed by
    // the browser, and on Mobile Safari a killed-during-navigation XHR rejects
    // as `AxiosError: Network Error` instead of `CanceledError` - which
    // PostHog's XHR-patched exception autocapture then records as a real
    // exception. Aborting from JS keeps us on axios's cancellation path.
    const sessionCheckController = new AbortController();

    const checkCurrentSession = async () => {
      try {
        const supabase = await getSupabase();
        const { data: { session }, error } = await supabase.auth.getSession();
        if (session && !error) {
          const userData = {
            id: session.user.id,
            email: session.user.email,
            created_at: session.user.created_at
          };

          setUser(userData);
          axios.defaults.headers.common['Authorization'] = `Bearer ${session.access_token}`;

          // Analytics will be initialized when needed
          return;
        }

        // Email sign-in goes through the backend (`signInWithEmail`), which stores
        // the JWT in the HttpOnly `auth_session` cookie and leaves the
        // Supabase browser client without a session. The session lives
        // server-side; the client restores user state on reload by calling
        // /api/auth/me with credentials: 'include' so the cookie is sent.
        // Without this branch `getSession()` is empty on every reload,
        // `user` stays null, and PrivateRoute bounces the signed-in user
        // to the home page.
        try {
          const meResponse = await fetchCurrentUser(sessionCheckController.signal);
          if (meResponse.data && meResponse.data.id) {
            setUser({
              id: meResponse.data.id,
              email: meResponse.data.email,
              // /me serialises this as `createdAt`; keep reading `created_at`
              // too so either shape populates the field.
              created_at: meResponse.data.createdAt ?? meResponse.data.created_at,
              emailVerified: meResponse.data.emailVerified === true,
            });
          }
        } catch (_meErr) {
          // /me said the user isn't signed in (401/403/404), or kept failing
          // after retries - either way, no session.
        }
      } catch (error) {
        console.error('Error checking session:', error);
      } finally {
        setLoading(false);
        setAuthChecked(true);
      }
    };

    // Add a timeout to ensure loading is always set to false. This only
    // unblocks spinners; it does not set authChecked, because a timeout is not
    // an answer and must not send a signed-in user to the home page.
    const timeoutId = setTimeout(() => {
      setLoading(false);
    }, 3000); // 3 second timeout

    checkCurrentSession().finally(() => {
      clearTimeout(timeoutId);
    });

    // Register the auth listener lazily so the @supabase/ssr SDK only
    // loads when we actually need a session check. useEffect callbacks
    // can't be async directly, so we wrap the listener registration in
    // an IIFE and stash the subscription in a ref for cleanup. DEL-771.
    const authListenerRef: { current: { subscription: { unsubscribe: () => void } } | null } = { current: null };
    void (async () => {
      const supabase = await getSupabase();
      const { data: authListener } = supabase.auth.onAuthStateChange(async (event: any, session: any) => {
      console.log('Auth state change:', { event, userId: session?.user?.id, provider: session?.user?.app_metadata?.provider });

      if (event === 'SIGNED_IN' && session?.user) {
        const token = session.access_token;
        const userData = {
          id: session.user.id,
          email: session.user.email,
          created_at: session.user.created_at
        };

        const isOAuthUser = session.user.app_metadata?.provider &&
          session.user.app_metadata.provider !== 'email';

        console.log('OAuth user detection:', {
          provider: session.user.app_metadata?.provider,
          createdAt: session.user.created_at,
          isOAuthUser,
          userId: session.user.id
        });

        // Only trigger OAuth setup for truly new users, not existing users with portfolios
        if (isOAuthUser) {
          // Check if OAuth setup is already in progress or completed
          const setupInProgress = localStorage.getItem('oauth_setup_in_progress');
          const setupCompleted = sessionStorage.getItem('oauth_setup_completed');
          
          if (setupInProgress || setupCompleted) {
            console.log('OAuth setup already in progress or completed, skipping duplicate setup');
            return;
          }
          
          // Mark setup as in progress to prevent duplicates
          localStorage.setItem('oauth_setup_in_progress', 'true');
          console.log('Set oauth_setup_in_progress flag to prevent duplicates');
          // Check if user already has portfolios before triggering OAuth setup
          try {
            console.log('Checking if OAuth user already has portfolios:', userData.id);
            
            const portfoliosResponse = await fetch('/api/portfolios', {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            
            if (portfoliosResponse.ok) {
              const portfolios = await portfoliosResponse.json();
              console.log('OAuth user portfolios found:', portfolios);
              
              // If user already has portfolios, don't run OAuth setup
              if (portfolios && portfolios.length > 0) {
                console.log('OAuth user already has portfolios, skipping OAuth setup');
                return;
              }
            } else if (portfoliosResponse.status === 401) {
              // If we get 401, the token might be invalid or auth is still settling
              // Don't assume no portfolios - proceed with setup to handle this case
              console.log('Portfolio check returned 401, proceeding with OAuth setup to handle auth settling');
            } else {
              console.log('Portfolio check failed with status:', portfoliosResponse.status);
            }
          } catch (error) {
            console.error('Error checking OAuth user portfolios:', error);
            // If we can't check portfolios, proceed with setup to be safe
          }

          // Only proceed with OAuth setup for truly new users
          // await storeUTMParameters(userData.id);

          // The signup event is captured server-side by setup-oauth-user (#430).

// Create default portfolio and send notifications for new OAuth users
          let setupResult = null;
          try {
            console.log('Calling new OAuth user setup API for:', userData.id);
            
            const response = await fetch('/api/auth/setup-new-user', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                userId: userData.id,
                email: userData.email,
                provider: session.user.app_metadata?.provider,
                // #430: first-touch context for the server-side signup event.
                acquisition: getUTMForAnalytics(),
              })
            });

            console.log('OAuth setup API response:', response.status, response.statusText);
            
            if (!response.ok) {
              const errorData = await response.text();
              console.error('OAuth setup API error:', errorData);
            } else {
              setupResult = await response.json();
              console.log('OAuth setup successful, result:', setupResult);
            }
          } catch (setupError) {
            console.error('Error setting up new OAuth user:', setupError);
            // Don't fail the login, just log the error
          } finally {
            // Always clear the setup flag after API completes (success or failure)
            localStorage.removeItem('oauth_setup_in_progress');
            // Mark setup as completed for this session to prevent duplicates
            sessionStorage.setItem('oauth_setup_completed', 'true');
            console.log('Cleared oauth_setup_in_progress flag and marked setup as completed');
          }
          
          // Process the setup result (if any) after clearing the flag
          if (setupResult) {
            // Only set force refresh flag if OAuth setup actually created something new
            // (i.e., if we have portfolio/scenario data in response)
            if (setupResult.defaultScenarioId || setupResult.portfolioId) {
              console.log('OAuth setup created new data, setting force refresh flag');
              localStorage.setItem('force_dashboard_refresh', 'true');
              
              // Store scenario data for dashboard to use
              if (setupResult.defaultScenarioId) {
                console.log('Setting default scenario for new portfolio:', setupResult.defaultScenarioId);
                console.log('Scenario data being stored:', {
                  defaultScenarioId: setupResult.defaultScenarioId,
                  defaultScenarioName: setupResult.defaultScenarioName,
                  portfolioId: setupResult.portfolioId,
                  portfolioName: setupResult.portfolioName
                });
                localStorage.setItem('new_default_scenario_id', setupResult.defaultScenarioId);
                localStorage.setItem('new_default_scenario_name', setupResult.defaultScenarioName);
                localStorage.setItem('new_portfolio_id', setupResult.portfolioId);
                localStorage.setItem('new_portfolio_name', setupResult.portfolioName);
              }
            } else {
              console.log('OAuth setup completed but no new portfolio/scenario created (user already existed), skipping refresh');
            }
          }

          // Apply stored referral code for OAuth users
          try {
            const referralResult = await applyStoredReferralCode();
            if (referralResult.success) {
              console.log('Referral code applied for OAuth user');
            }
          } catch (referralError) {
            console.error('Error applying referral code for OAuth user:', referralError);
          }
        }

        setUser(userData);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        // Analytics will be initialized when needed

        // Track experiment conversions (e.g., hero headline A/B test) - for OAuth logins
        trackExperimentConversions();

      } else if (event === 'TOKEN_REFRESHED' && session?.user) {
        // Update stored tokens when they refresh
        const token = session.access_token;
        const userData = {
          id: session.user.id,
          email: session.user.email,
          created_at: session.user.created_at
        };

        setUser(userData);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        syncSessionCookie(session);

      } else if (event === 'TOKEN_REFRESHED') {
        if (!session) {
          // Token refresh failed, sign out user only if they were already logged in
          const wasSignedIn = userRef.current !== null;
          if (wasSignedIn) {
            console.warn('[Auth] Token refresh failed, signing out user');
            // #505: tell the server, which records it under the user from the
            // HttpOnly session cookie even when browser analytics are blocked.
            apiRequest('POST', '/auth/session-refresh-failed').catch(() => {});
            Cookies.remove('token');
            Cookies.remove('user_signed_up_tracked');
            delete axios.defaults.headers.common['Authorization'];
            setUser(null);
          } else {
            console.warn('[Auth] TOKEN_REFRESHED event with no session but no logged-in user - ignoring');
          }
        } else if (session?.user) {
          // Token was successfully refreshed, update cookies
          console.log('[Auth] Token refreshed successfully');
        }

      } else if (event === 'SIGNED_OUT') {
        Cookies.remove('token');
        Cookies.remove('user_signed_up_tracked');
        delete axios.defaults.headers.common['Authorization'];
        setUser(null);
      } else if (event === 'INITIAL_SESSION') {
        if (session?.user) {
          const token = session.access_token;
          if (wasJustIssued(token)) {
            syncSessionCookie(session);
          }
          const userData = {
            id: session.user.id,
            email: session.user.email,
            created_at: session.user.created_at
          };

          const isOAuthUser = session.user.app_metadata?.provider &&
            session.user.app_metadata.provider !== 'email';

          console.log('INITIAL_SESSION OAuth user detection:', {
            provider: session.user.app_metadata?.provider,
            createdAt: session.user.created_at,
            isOAuthUser,
            userId: session.user.id
          });

          // Only trigger OAuth setup for truly new users, not existing users with portfolios
          if (isOAuthUser) {
            // Check if OAuth setup is already in progress or completed
            const setupInProgress = localStorage.getItem('oauth_setup_in_progress');
            const setupCompleted = sessionStorage.getItem('oauth_setup_completed');
            
            if (setupInProgress || setupCompleted) {
              console.log('OAuth setup already in progress or completed (INITIAL_SESSION), skipping duplicate setup');
              return;
            }
            
            // Mark setup as in progress to prevent duplicates
            localStorage.setItem('oauth_setup_in_progress', 'true');
            console.log('Set oauth_setup_in_progress flag to prevent duplicates (INITIAL_SESSION)');
            // Check if user already has portfolios before triggering OAuth setup
            try {
              console.log('Checking if OAuth user already has portfolios from INITIAL_SESSION:', userData.id);
              
              const portfoliosResponse = await fetch('/api/portfolios', {
                method: 'GET',
                headers: {
                  'Authorization': `Bearer ${token}`
                }
              });
              
              if (portfoliosResponse.ok) {
                const portfolios = await portfoliosResponse.json();
                console.log('OAuth user portfolios found from INITIAL_SESSION:', portfolios);
                
                // If user already has portfolios, don't run OAuth setup
                if (portfolios && portfolios.length > 0) {
                  console.log('OAuth user already has portfolios from INITIAL_SESSION, skipping OAuth setup');
                  return;
                }
              } else if (portfoliosResponse.status === 401) {
                // If we get 401, the token might be invalid or auth is still settling
                // Don't assume no portfolios - proceed with setup to handle this case
                console.log('Portfolio check from INITIAL_SESSION returned 401, proceeding with OAuth setup to handle auth settling');
              } else {
                console.log('Portfolio check from INITIAL_SESSION failed with status:', portfoliosResponse.status);
              }
            } catch (error) {
              console.error('Error checking OAuth user portfolios from INITIAL_SESSION:', error);
              // If we can't check portfolios, proceed with setup to be safe
            }

            // Only proceed with OAuth setup for truly new users
            // await storeUTMParameters(userData.id);
            
            let setupResult = null;
            try {
              console.log('Calling OAuth setup from INITIAL_SESSION for:', userData.id);
              
              const response = await fetch('/api/auth/setup-new-user', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                  userId: userData.id,
                  email: userData.email,
                  provider: session.user.app_metadata?.provider,
                  // #430: first-touch context for the server-side signup event.
                  acquisition: getUTMForAnalytics(),
                })
              });

              console.log('OAuth setup API response from INITIAL_SESSION:', response.status);
              if (response.ok) {
                setupResult = await response.json();
                console.log('OAuth setup successful from INITIAL_SESSION, result:', setupResult);
              }
            } catch (setupError) {
              console.error('Error setting up OAuth user from INITIAL_SESSION:', setupError);
            } finally {
              // Always clear setup flag after API completes (success or failure)
              localStorage.removeItem('oauth_setup_in_progress');
              // Mark setup as completed for this session to prevent duplicates
              sessionStorage.setItem('oauth_setup_completed', 'true');
              console.log('Cleared oauth_setup_in_progress flag and marked setup as completed from INITIAL_SESSION');
            }
            
            // Process the setup result (if any) after clearing the flag
            if (setupResult) {
              // Only set force refresh flag if OAuth setup actually created something new
              // (i.e., if we have portfolio/scenario data in the response)
              if (setupResult.defaultScenarioId || setupResult.portfolioId) {
                console.log('OAuth setup from INITIAL_SESSION created new data, setting force refresh flag');
                localStorage.setItem('force_dashboard_refresh', 'true');
                
                // Store scenario data for the dashboard to use
                if (setupResult.defaultScenarioId) {
                  console.log('Setting default scenario for new portfolio from INITIAL_SESSION:', setupResult.defaultScenarioId);
                  console.log('Scenario data being stored from INITIAL_SESSION:', {
                    defaultScenarioId: setupResult.defaultScenarioId,
                    defaultScenarioName: setupResult.defaultScenarioName,
                    portfolioId: setupResult.portfolioId,
                    portfolioName: setupResult.portfolioName
                  });
                  localStorage.setItem('new_default_scenario_id', setupResult.defaultScenarioId);
                  localStorage.setItem('new_default_scenario_name', setupResult.defaultScenarioName);
                  localStorage.setItem('new_portfolio_id', setupResult.portfolioId);
                  localStorage.setItem('new_portfolio_name', setupResult.portfolioName);
                  console.log('localStorage set with scenario data from INITIAL_SESSION');
                }
              } else {
                console.log('OAuth setup from INITIAL_SESSION completed but no new portfolio/scenario created (user already existed), skipping refresh');
              }
            }
          }

          setCustomToken(token);
          setUser(userData);
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

          // Analytics will be initialized when needed

        }
      }
    });
      authListenerRef.current = authListener;
    })();

    // Add axios interceptor to handle authentication errors
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Only sign out on clear authentication failures
        // 401: Always authentication error
        // 403: Only if not from signin (which can be "requires verification")
        // 400: Only from signin endpoint (invalid credentials)
        const shouldSignOut =
          error.response?.status === 401 ||
          (error.response?.status === 403 && !error.config?.url?.includes('/api/auth/email/signin')) ||
          (error.response?.status === 400 && error.config?.url?.includes('/api/auth/email/signin'));

        if (shouldSignOut) {
          console.log('Authentication error detected, signing out user');
          // Use the ref to call signOut to avoid stale closure issues
          // if (signOutRef.current) {
          //   signOutRef.current().catch(console.error);
          // }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      authListenerRef.current?.subscription.unsubscribe();
      // Clean up the interceptor
      axios.interceptors.response.eject(interceptor);
      // DEL-813: abort any in-flight /api/auth/me so a fast /login bounce
      // doesn't reach PostHog as a Network Error (see comment above).
      sessionCheckController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signInWithGoogle = async () => {
    try {
      const apiUrl = process.env.NEXT_PUBLIC_API_URL || '';
      // #479: the callback comes back from Google, so it cannot see the
      // visitor's stored first touch. Hand it to the server up front; it is
      // held with the PKCE session and used if this turns out to be a signup.
      // The ?ref= referral code travels the same way, so the server can
      // credit the referrer when the account is created (#479).
      const storedReferralCode = typeof window !== 'undefined' ? localStorage.getItem('referral_code') : null;
      const attribution = new URLSearchParams(
        Object.entries({
          ...getUTMForAnalytics(),
          ...(storedReferralCode ? { referral_code: storedReferralCode } : {}),
        }).filter(([, v]) => v)
      ).toString();
      const response = await fetch(`${apiUrl}/auth/google${attribution ? `?${attribution}` : ''}`, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to initiate Google OAuth');
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const script = doc.querySelector('script');
      
      if (script && script.textContent) {
        const scriptText = script.textContent;
        const sessionIdMatch = scriptText.match(/const sessionId\s*=\s*['"]([^'"]+)['"]/);
        const clientIdMatch = scriptText.match(/client_id:\s*['"]([^'"]+)['"]/);
        const redirectUriMatch = scriptText.match(/redirect_uri:\s*['"]([^'"]+)['"]/);
        const challengeMatch = scriptText.match(/code_challenge:\s*['"]([^'"]+)['"]/);
        
        if (sessionIdMatch && clientIdMatch && redirectUriMatch && challengeMatch) {
          const sessionId = sessionIdMatch[1];
          const clientId = clientIdMatch[1];
          const redirectUri = redirectUriMatch[1];
          const challenge = challengeMatch[1];
          
          // The PKCE verifier stays on the server; `state` carries only the
          // session id, which the callback checks against a cookie.
          const params = new URLSearchParams({
            client_id: clientId,
            redirect_uri: redirectUri,
            response_type: 'code',
            scope: 'openid email profile',
            code_challenge: challenge,
            code_challenge_method: 'S256',
            state: JSON.stringify({ sessionId })
          });
          
          window.location.href = 'https://accounts.google.com/o/oauth2/v2/auth?' + params.toString();
        } else {
          throw new Error('Failed to parse OAuth parameters');
        }
      } else {
        throw new Error('OAuth redirect script not found');
      }

      return { data: null, error: null };
    } catch (error: unknown) {
      return { data: null, error };
    }
  };

  const signInWithApple = async () => {
    try {
      const apiUrl = process.env.NEXT_PUBLIC_API_URL || '';
      const response = await fetch(`${apiUrl}/auth/apple`, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to initiate Apple OAuth');
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const script = doc.querySelector('script');
      
      if (script && script.textContent) {
        const scriptText = script.textContent;
        const verifierMatch = scriptText.match(/const verifier\s*=\s*['"]([^'"]+)['"]/);
        const sessionIdMatch = scriptText.match(/const sessionId\s*=\s*['"]([^'"]+)['"]/);
        const clientIdMatch = scriptText.match(/client_id:\s*['"]([^'"]+)['"]/);
        const redirectUriMatch = scriptText.match(/redirect_uri:\s*['"]([^'"]+)['"]/);
        const challengeMatch = scriptText.match(/code_challenge:\s*['"]([^'"]+)['"]/);
        
        if (verifierMatch && sessionIdMatch && clientIdMatch && redirectUriMatch && challengeMatch) {
          const verifier = verifierMatch[1];
          const sessionId = sessionIdMatch[1];
          const clientId = clientIdMatch[1];
          const redirectUri = redirectUriMatch[1];
          const challenge = challengeMatch[1];
          
          sessionStorage.setItem('pkce_verifier', verifier);
          sessionStorage.setItem('pkce_session_id', sessionId);
          
          const params = new URLSearchParams({
            client_id: clientId,
            redirect_uri: redirectUri,
            response_type: 'code',
            scope: 'openid email name',
            code_challenge: challenge,
            code_challenge_method: 'S256',
            state: JSON.stringify({ sessionId, verifier })
          });
          
          window.location.href = 'https://appleid.apple.com/auth/authorize?' + params.toString();
        } else {
          throw new Error('Failed to parse OAuth parameters');
        }
      } else {
        throw new Error('OAuth redirect script not found');
      }

      return { data: null, error: null };
    } catch (error: unknown) {
      return { data: null, error };
    }
  };

  const signInWithEmail = async (email: string, password: string) => {
    try {
      const storedReferralCode = typeof window !== 'undefined'
        ? localStorage.getItem('referral_code')
        : null;

      const response = await axios.post('/api/auth/email/signin', {
        email,
        password,
        referralCode: storedReferralCode,
        // Joins the server-side signup event to this browser session (DEL tracking fix).
        posthog_session_id: getPostHogSessionId?.(),
        // #430: first-touch context, so the server-side signup event is
        // attributed even when the visitor declined analytics cookies.
        ...getUTMForAnalytics(),
      });

      if (response.data.requiresVerification) {
        return response.data;
      }

      const { user, token } = response.data;

      setCustomToken(token);
      setUser(user);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // DEL-1812: re-fetch /api/auth/me now that the auth_session cookie is
      // committed, so the AuthContext has the rich user payload
      // (emailVerified). Without this, the initial /me round-trip ran on
      // /login while the user was still anonymous and returned 401; the
      // soft-navigated /dashboard mount reuses the same AuthProvider
      // instance, so /me is never called again and the user state stays
      // as the bare `{id, email, created_at}` returned by the signin
      // body. The dashboard then renders without emailVerified - which is
      // what made a brand-new sign-up look worse than an anonymous
      // visitor (no trial, no onboarding, no "verify your email" prompt).
      //
      // Fire-and-forget: a failure here must not unwind an
      // already-successful signin. The bearer-and-cookie credentials are
      // already proven by the 200 we just received from signin; if the
      // cookie side is broken, the basic user object is still enough to
      // render the dashboard. Not awaited: blocking on it delayed the
      // post-signin redirect by a full extra round-trip.
      fetchCurrentUser()
        .then((meResponse) => {
          if (meResponse.data && meResponse.data.id) {
            setUser({
              id: meResponse.data.id,
              email: meResponse.data.email,
              created_at: meResponse.data.createdAt ?? meResponse.data.created_at,
              emailVerified: meResponse.data.emailVerified === true,
            });
          }
        })
        .catch(() => {
          // /me is best-effort here. The signin itself already succeeded.
        });

      // Analytics will be initialized when needed

      // Track experiment conversions (e.g., hero headline A/B test)
      trackExperimentConversions();

      return response.data;
    } catch (error: unknown) {
      if ((error as { response?: { data?: { requiresVerification?: boolean } } })?.response?.data?.requiresVerification) {
        return (error as { response?: { data?: { requiresVerification?: boolean } } }).response?.data;
      }
      throw error;
    }
  };

  const signUpWithEmail = async (email: string, password: string) => {
    try {
      const storedReferralCode = typeof window !== 'undefined' 
        ? localStorage.getItem('referral_code') 
        : null;

      console.log('Signup attempt with referral code:', storedReferralCode);

      const response = await axios.post('/api/auth/email/signup', {
        email,
        password,
        referralCode: storedReferralCode,
        // Joins the server-side signup event to this browser session (DEL tracking fix).
        posthog_session_id: getPostHogSessionId?.(),
        // #430: first-touch context, so the server-side signup event is
        // attributed even when the visitor declined analytics cookies.
        ...getUTMForAnalytics(),
      });

      if (response.data.user && !response.data.requiresVerification) {
        const userData = response.data.user;

        await storeUTMParameters(userData.id);

        // The signup event is captured server-side by /email/signup (#430).

        // Track experiment conversions (e.g., hero headline A/B test)
        trackExperimentConversions();

        if (storedReferralCode) {
          localStorage.removeItem('referral_code');
        }
      }

      return response.data;
    } catch (error: unknown) {
      throw error;
    }
  };

  const checkPasskeyAvailability = async (email: string): Promise<boolean> => {
    try {
      const response = await axios.post('/api/2fa/webauthn/login/start', { email });
      return response.data.options !== undefined;
    } catch (_error) {
      return false;
    }
  };

  const signInWithPasskey = async (email: string) => {
    try {
      const storedReferralCode = typeof window !== 'undefined' 
        ? localStorage.getItem('referral_code') 
        : null;

      // Start WebAuthn authentication
      const startResponse = await axios.post('/api/2fa/webauthn/login/start', { email });
      const { options, userId, userEmail } = startResponse.data;

      // The endpoint answers 200 with `available: false` when the email has no
      // passkey, so guard here rather than handing `undefined` to
      // startAuthentication and getting an opaque WebAuthn error.
      if (!options) {
        throw new Error('No passkey is registered for this email');
      }

      // Get credential from user. Dynamically resolve the WebAuthn client
      // so the @simplewebauthn/browser bundle (~28 KB minified) does not
      // ship in the /dashboard initial bundle. The await blocks on the
      // user-facing passkey click, so the extra network round-trip is
      // invisible to the user. DEL-892.
      const { startAuthentication } = await import('@simplewebauthn/browser');
      const credential = await startAuthentication(options);

      // Complete authentication
      const finishResponse = await axios.post('/api/2fa/webauthn/login/finish', {
        userId,
        body: credential
      });

      if (finishResponse.data.success) {
        // Use the real Supabase token from the server
        const token = finishResponse.data.token;

        if (!token) {
          throw new Error('No token returned from server');
        }

        const userData = {
          id: userId,
          email: userEmail,
          created_at: new Date().toISOString()
        };

        // Set cookies and headers
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        setUser(userData);

        // Apply referral code if present
        if (storedReferralCode) {
          try {
            await applyStoredReferralCode();
            localStorage.removeItem('referral_code');
          } catch (referralError) {
            console.error('Error applying referral code:', referralError);
          }
        }

        // Store UTM parameters
        await storeUTMParameters(userData.id);

        // Track sign up
        trackEvent('passkey_login', {
          method: 'webauthn',
          email: userEmail
        });

        // Track experiment conversions (e.g., hero headline A/B test)
        trackExperimentConversions();

        return { user: userData, token };
      } else {
        throw new Error('Passkey authentication failed');
      }
    } catch (error: unknown) {
      console.error('Passkey sign in error:', error);
      throw error;
    }
  };

  const signOut = async (reason: string = 'unspecified', detail: Record<string, unknown> = {}) => {
    trackSessionEnded(reason, detail);
    try {
      // End the session on the server first. It lives in the HttpOnly
      // auth_session cookie, which page scripts cannot clear, so everything
      // below only tidies client state. Without this call, signing out left
      // the cookie valid for up to 7 days and the next visit was signed
      // straight back in - on a shared computer, as the previous user.
      // Best effort: a failure here must not trap anyone in a signed-in UI.
      try {
        await apiRequest('POST', '/auth/clear-session', { reason });
      } catch (error) {
        console.error('Failed to clear the server session on sign out:', error);
      }
      // 'local' ends this browser's Supabase session only. The default,
      // 'global', revokes every session the user has, so signing out on one
      // device signed Google/Apple users out everywhere.
      const supabase = await getSupabase();
      await supabase.auth.signOut({ scope: 'local' });

      // Clear the in-memory custom JWT (email/password flow) so
      // getAccessToken() doesn't keep returning it after signout.
      clearCachedAccessToken();
      delete axios.defaults.headers.common['Authorization'];

      // Clear all cookies
      Cookies.remove('token');
      Cookies.remove('user_signed_up_tracked');
      Cookies.remove('activePortfolioId');
      Cookies.remove('affiliateReturnUrl');
      
      // Clear all localStorage items
      if (typeof window !== 'undefined') {
        localStorage.removeItem('activePortfolio');
        localStorage.removeItem('selectedScenario');
        // DEL-755: also clear the per-portfolio pattern that ScenarioContext
        // writes (`selectedScenario_${activePortfolio?.id}`). Without this the
        // pre-logout scenario leaks into the next session and the dashboard
        // can render a mix of entries.
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const key = localStorage.key(i);
          if (key && key.startsWith('selectedScenario_')) {
            localStorage.removeItem(key);
          }
        }
        localStorage.removeItem('preferredCurrency');
        localStorage.removeItem('valueMaskingEnabled');
        // Cookie consent (delphina_cookie_consent / cookie_consent_given) is
        // the device's choice, not the account's, so it survives sign-out.
        // Clearing it here turned PostHog off after the redirect: no
        // pageviews on / or /login until the next sign-in re-accepted, and
        // resetPostHogUser (consent-gated) never ran, so the next person on
        // this device inherited the previous distinct_id.
        localStorage.removeItem('alpha-banner-dismissed');
        localStorage.removeItem('transactionsShowGraphs');
        localStorage.removeItem('utm_parameters');
        localStorage.removeItem('new_asset_added');
        localStorage.removeItem('new_asset_id');
        localStorage.removeItem('new_asset_timestamp');
        localStorage.removeItem('force_dashboard_refresh');
        localStorage.removeItem('refresh_timestamp');
        localStorage.removeItem('oauth_setup_in_progress');
      }
      
      // Clear sessionStorage items
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem('oauth_setup_completed');
      }
      
      // Clear axios authorization header
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
      
      // Redirect to home page
      window.location.href = '/';
    } catch (error: unknown) {
      throw error;
    }
  };

  // Set the ref to the signOut function
  useEffect(() => {
    signOutRef.current = signOut;
  }, [signOut]);

  const checkImpersonationStatus = async () => {
    try {
      // No pre-flight token check: the session is an HttpOnly cookie, so an
      // unauthenticated caller simply gets a 401 and lands in the catch.
      const data = await apiRequest<{ session?: {
        id: string; target_user_id: string; target_email: string; started_at: string;
      } }>('GET', '/admin/impersonation/active');

      if (data.session) {
        setImpersonationSession({
          id: data.session.id,
          targetUserId: data.session.target_user_id,
          targetEmail: data.session.target_email,
          startedAt: data.session.started_at,
        });
        localStorage.setItem('impersonation_session', JSON.stringify(data.session));
      } else {
        setImpersonationSession(null);
        localStorage.removeItem('impersonation_session');
      }
    } catch (error) {
      console.error('Error checking impersonation status:', error);
    }
  };

  const startImpersonation = async (targetUserId: string, targetEmail: string) => {
    // apiRequest throws on non-2xx; surface the server's message as before.
    let data: { sessionId: string; startedAt: string };
    try {
      data = await apiRequest<{ sessionId: string; startedAt: string }>(
        'POST',
        '/admin/impersonation/start',
        { targetUserId }
      );
    } catch (err) {
      const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      throw new Error(message || 'Failed to start impersonation');
    }

    setImpersonationSession({
      id: data.sessionId,
      targetUserId,
      targetEmail,
      startedAt: data.startedAt,
    });
    localStorage.setItem('impersonation_session', JSON.stringify({
      sessionId: data.sessionId,
      target_user_id: targetUserId,
      target_email: targetEmail,
      started_at: data.startedAt,
    }));
    
    // Force full page reload so SSR re-renders with impersonation headers
    console.log('[Impersonation] Session started, reloading page for SSR to pick up impersonation context');
    window.location.reload();
  };

  const endImpersonation = async () => {
    if (!impersonationSession) return;

    await apiRequest('POST', '/admin/impersonation/end', {
      sessionId: impersonationSession.id,
    });

    setImpersonationSession(null);
    localStorage.removeItem('impersonation_session');
  };

  // Check impersonation status on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('impersonation_session');
      if (stored) {
        try {
          const session = JSON.parse(stored);
          setImpersonationSession({
            id: session.sessionId,
            targetUserId: session.target_user_id,
            targetEmail: session.target_email,
            startedAt: session.started_at,
          });
        } catch (_e) {
          localStorage.removeItem('impersonation_session');
        }
      }
    }
  }, []);

  const value = {
    user,
    loading,
    authChecked,
    impersonationSession,
    startImpersonation,
    endImpersonation,
    checkImpersonationStatus,
    signInWithGoogle,
    signInWithApple,
    signInWithEmail,
    signInWithPasskey,
    checkPasskeyAvailability,
    signUpWithEmail,
    signOut,
    logout: signOut,
    getSupabase,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

const defaultAuthValue: AuthContextType = {
  user: null,
  loading: true,
  authChecked: false,
  impersonationSession: null,
  startImpersonation: async () => {},
  endImpersonation: async () => {},
  checkImpersonationStatus: async () => {},
  signInWithGoogle: async () => null,
  signInWithApple: async () => null,
  signInWithEmail: async () => {},
  signInWithPasskey: async () => {},
  checkPasskeyAvailability: async () => false,
  signUpWithEmail: async () => {},
  signOut: async () => {},
  logout: async () => {},
  getSupabase,
};

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === null || context === undefined) {
    return defaultAuthValue;
  }
  return context;
}
